// Import to include in project to recognize the table token types
import 'micromark-extension-gfm-table'
// Import to include in project to recognize the task list item token types
import 'micromark-extension-gfm-task-list-item'

import type {Delimiter, DelimiterTokenType} from 'mdast'
import type {CompileContext, Extension, Handle} from 'mdast-util-from-markdown'
import type {Token, TokenType} from 'micromark-util-types'

import {isPositionedNode} from '../../syntax-tree/positioned-node'

/**
 * Attaches syntactic delimiter data to the tree by inserting `Delimiter` nodes. Results in a tree with delimiter data
 * that can be present in any node type -- even types that wouldn't normally contain `children`.
 *
 * @example
 * This Markdown source:
 * ```md
 * > ----
 * ```
 *
 * Will result in a tree like:
 * ```js
 * {
 *   type: "root",
 *   position: {...},
 *   children: [
 *     {
 *       type: "quote",
 *       position: {...},
 *       children: [
 *         {type: "delimiter", tokenType: "quoteMarker", raw: "> ", position: {...}},
 *         {
 *           type: "thematicBreak"
 *           position: {...},
 *           // thematic breaks would not normally allow `children`
 *           children: [
 *             {type: "delimiter", tokenType: "thematicBreakSequence", raw: "----", position: {...}}
 *           ]
 *         },
 *       ]
 *     }
 *   ]
 * }
 * ```
 */
export function mdastDelimiters(): Extension {
  return {
    enter: {
      ...Object.fromEntries(delimiterTokenTypes.map(type => [type, handleDelimiterEnter])),
      linePrefix: handleLinePrefixEnter,
    },
    exit: {
      ...Object.fromEntries(delimiterTokenTypes.map(type => [type, handleDelimiterExit])),
      linePrefix: handleLinePrefixExit,
    },
  }
}

/** All Micromark token types that are always treated as delimiters. */
const delimiterTokenTypes = [
  'blockQuotePrefix',
  'strongSequence',
  'emphasisSequence',
  'codeTextSequence', // inline code delimiters
  'codeTextPadding', // optional space inside inline code
  'strikethroughSequence',
  'thematicBreakSequence',
  'codeFencedFence',
  'atxHeadingSequence', // ### heading indicator
  'whitespace', // "Generic type for syntactic whitespace" (notably the space between ATX heading delimiter and text)
  'setextHeadingLine', // ===like or --- heading underline
  'labelMarker', // brackets around link text/image alt text
  'labelImageMarker', // ! for image
  'resource', // link/image (url) part
  'resourceDestination',
  'linePrefix', // indentation for indented code blocks
  'listItemIndent',
  'listItemPrefix',
  'taskListCheck',
  'tableCellDivider',
  'tableDelimiterRow', // |---|---| required second row in a table
  'tableDelimiter', // cells inside tableDelimiter row
  'autolinkMarker',
  'hardBreakEscape', // \ backslash escape at end of line to force a break
  'escapeMarker', // \ backslash escape for markdown character
  'characterReference', // &whatever; HTML escapes
] as const satisfies TokenType[]

// Extend the mdast tree types to allow any node to contain delimiter nodes
declare module 'mdast' {
  /** All of the `micromark` or HTML token types that can be used as delimiter nodes. */
  // htmlTag delimiters are added by the `split-html` extension
  export type DelimiterTokenType = (typeof delimiterTokenTypes)[number] | 'htmlTag'

  /**
   * Represents the location of a syntactic delimiter in the source text. Delimiters can be essentially any text that
   * represents Markdown syntax and would be filtered from the rendered result - the asterisks around bold text, the
   * carets in front of blockquotes, the backticks around fenced code blocks, etc.
   *
   * Any node except delimiters, even nodes that cannot normally contain other kinds of children, can contain delimiter
   * nodes.
   */
  export interface Delimiter extends Node, DelimiterParent {
    type: 'delimiter'
    /** A delimiter's `tokenType` is the Micromark token type it was created from. */
    tokenType: DelimiterTokenType
    /** Raw source code text for this delimiter. */
    raw: string
  }

  /** Delimiters are valid in places where content wouldn't normally be accepted, so they are their own content category. */
  export interface DelimiterContentMap {
    delimiter: Delimiter
  }

  export type DelimiterContent = DelimiterContentMap[keyof DelimiterContentMap]

  /** `DelimiterParent` nodes can *only* contain delimiters. */
  export interface DelimiterParent {
    children?: DelimiterContent[]
  }

  // Delimiters are valid _everywhere_, so we register them with every built-in content map
  interface BlockContentMap extends DelimiterContentMap {}
  interface DefinitionContentMap extends DelimiterContentMap {}
  interface FrontmatterContentMap extends DelimiterContentMap {}
  interface ListContentMap extends DelimiterContentMap {}
  interface PhrasingContentMap extends DelimiterContentMap {}
  interface RootContentMap extends DelimiterContentMap {}
  interface RowContentMap extends DelimiterContentMap {}
  interface TableContentMap extends DelimiterContentMap {}

  // Delimiters are also valid in places where content is not normally valid
  interface Literal extends DelimiterParent {}

  // There are a few node types that don't allow content but also aren't `Literal`s so we have to handle them separately
  interface Break extends DelimiterParent {}
  interface Definition extends DelimiterParent {}
  interface ThematicBreak extends DelimiterParent {}
  interface Image extends DelimiterParent {}
  interface ImageReference extends DelimiterParent {}
  interface FootnoteReference extends DelimiterParent {}

  /** Nodes that don't normally accept children, and now only accept delimiters as children. */
  export type DelimiterParents = Extract<Nodes, DelimiterParent>
}

// Extend the mdast-util-from-markdown internal types to support some custom context fields for our use
declare module 'mdast-util-from-markdown' {
  interface CompileData {
    /** Stores the most recently exited delimiter token. Used to determine if a line ending should be treated as a delimiter. */
    lastExitedDelimiterToken?: Token
    /** Stores the current delimiter stack. The last item in the stack is the lowest currently-open delimiter node. */
    delimiterStack?: Delimiter[]
  }
}

/** Returns the lowest open node on the stack - the one that we should push children to. */
function getParent(context: CompileContext) {
  // First look for any open delimiters in the delimiter stack
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  if (context.data.delimiterStack?.length) return context.data.delimiterStack.at(-1)!

  const parent = context.stack.at(-1)

  // mdast uses Fragment nodes as temporary buckets to push things to. For most nodes we can just push to the fragment
  // and mdast will keep our data when it moves those nodes to the correct parent. But for Literal nodes (nodes with
  // a 'value' property), mdast will discard nodes we push to the fragment as it builds the plain string value. So in
  // that case we return the fragment's parent (the Literal node) instead.
  if (parent?.type === 'fragment') {
    const grandparent = context.stack.at(-2)
    if (grandparent && 'value' in grandparent) return grandparent
  }

  return parent
}

/** Create a delimiter node and push it to the currently open node. */
const handleDelimiterEnter: Handle = function (this, token) {
  const delimiter: Delimiter = {
    type: 'delimiter',
    tokenType: token.type as DelimiterTokenType,
    raw: this.sliceSerialize(token),
    position: {
      start: token.start,
      end: token.end,
    },
  }

  // Normally, we could just enter the delimiter using the built-in helper: `this.enter(delimiter, token)`
  // However, this would push the delimiter onto the main stack, which breaks the library's internal expectations.
  // For example, when the library handles a `resourceDestinationString` token, it expects the last open node on the
  // stack to be an `image` or `link` (and will throw an exception otherwise). So if we push delimiters to the main
  // stack, the library will crash. To work around this, we maintain a separate `delimiterStack` just for delimiters.
  const parent = getParent(this)
  if (parent) {
    parent.children ??= []
    parent.children.push(delimiter)

    this.data.delimiterStack ??= []
    this.data.delimiterStack.push(delimiter)
  }

  // Return false to fall through to the default logic to allow mdast to still create regular nodes from this token
  return false
}

/** Remove the delimiter from the stack to close it out. */
const handleDelimiterExit: Handle = function (this, token) {
  const delimiter = this.data.delimiterStack?.pop()

  // This should be impossible, because we should never exit a node we didn't just enter
  if (delimiter?.tokenType !== token.type) throw new Error(`Expected open '${token.type}' delimiter on stack`)

  this.data.lastExitedDelimiterToken = token

  return false
}

/**
 * Lists have an annoying quirk where an empty list item with a space following the marker will be parsed as
 * a `linePrefix` following a `listItemPrefix`, even though that space would usually be considered part of the
 * `listItemPrefix` if there was any content in the item. This is a pain to work with because we always have treat it
 * as an edge case.
 *
 * So rather than working around this everywhere in the code, we just drop the `linePrefix` in this case and extend the
 * `listItemPrefix` to cover the space.
 */
const handleLinePrefixEnter: Handle = function (this, token) {
  const parent = getParent(this)
  const lastChild = parent?.children?.at(-1)
  if (
    token.end.offset - token.start.offset === 1 &&
    lastChild?.type === 'delimiter' &&
    lastChild.tokenType === 'listItemPrefix' &&
    isPositionedNode(lastChild) &&
    token.start.offset === lastChild.position.end.offset
  ) {
    lastChild.position.end = {...token.end}
    lastChild.raw += this.sliceSerialize(token)
  } else {
    handleDelimiterEnter.call(this, token)
  }
  return false
}

/** Only exit `linePrefix` delimiters that were actually entered. */
const handleLinePrefixExit: Handle = function (this, token) {
  const parent = getParent(this)

  if (parent?.type === 'delimiter' && parent.tokenType === 'linePrefix') handleDelimiterExit.call(this, token)

  return false
}
